import { Check, ThumbsDown, ThumbsUp } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import {
  contestantLabel,
  formatMs,
  formatUsd,
  verdictFor,
  type LabelValue,
  type ModelLabResultDetail,
  type Task,
} from '@/lib/model-lab'
import { DecisionChip } from './badges'

interface Props {
  readonly task: Task
  readonly result: ModelLabResultDetail
  readonly label: LabelValue | undefined
  /** Adopt this model's decision as the label (non-SMS tasks). */
  readonly onAccept: (decision: string) => void
  /** Rate this model's SMS (SMS task). */
  readonly onRate: (rating: 'good' | 'bad') => void
  readonly saving: boolean
}

const FIELD_ORDER = [
  'pickup_address', 'dropoff_address', 'pickup_date', 'pickup_time', 'passenger_count',
  'vehicle_type', 'occasion_event', 'estimated_hours', 'service_type', 'customer_name', 'customer_email',
] as const

function AnalysisBody({ parsed }: { readonly parsed: Record<string, unknown> }) {
  const fields = (parsed.extracted_fields ?? {}) as Record<string, unknown>
  const filled = FIELD_ORDER.filter((k) => fields[k] !== null && fields[k] !== undefined && fields[k] !== '')
  return (
    <div className="space-y-2 text-xs">
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-muted-foreground">
        <span>Price: <strong className="text-foreground">{parsed.price_quoted == null ? '—' : `$${String(parsed.price_quoted)}`}</strong></span>
        <span>Confirmed: <strong className="text-foreground">{parsed.confirmed ? 'yes' : 'no'}</strong></span>
        <span>Paid: <strong className="text-foreground">{parsed.payment_confirmed ? 'yes' : 'no'}</strong></span>
        <span>Confidence: <strong className="text-foreground">{String(parsed.booking_confidence ?? '—')}</strong></span>
      </div>
      {typeof parsed.reasoning === 'string' && <p className="leading-relaxed">{parsed.reasoning}</p>}
      {filled.length > 0 && (
        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 rounded bg-muted/40 p-2">
          {filled.map((k) => (
            <div key={k} className="contents">
              <dt className="text-muted-foreground">{k.replace(/_/g, ' ')}</dt>
              <dd>{String(fields[k])}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  )
}

function GateBody({ parsed }: { readonly parsed: Record<string, unknown> }) {
  const decisions = (parsed.decisions ?? []) as Array<{ rowId: string; decision: string; reason: string }>
  const omitted = Number(parsed.omittedRows ?? 0)
  return (
    <ul className="space-y-0.5 text-xs">
      {omitted > 0 && (
        <li className="text-amber-700">
          Left {omitted} row{omitted === 1 ? '' : 's'} out — defaulted to send, as production would.
        </li>
      )}
      {decisions.map((d) => (
        <li key={d.rowId}>
          <span className="font-medium">{d.decision}</span> <span className="text-muted-foreground">— {d.reason}</span>
        </li>
      ))}
    </ul>
  )
}

function SmsBody({ parsed }: { readonly parsed: Record<string, unknown> }) {
  const message = String(parsed.message ?? '')
  return (
    <div className="space-y-1">
      <p className="rounded-lg bg-blue-50 p-2.5 text-sm leading-relaxed text-blue-950">{message}</p>
      <p className={`text-xs ${message.length > 160 ? 'text-amber-700' : 'text-muted-foreground'}`}>
        {message.length} characters{parsed.retried ? ' · needed the shorten-retry' : ''}
      </p>
    </div>
  )
}

export function ResultCard({ task, result, label, onAccept, onRate, saving }: Props) {
  const verdict = verdictFor(task, result, label)
  const rating = label?.ratings?.[result.contestant_key]
  const parsed = result.parsed

  return (
    <Card className={verdict === 'correct' ? 'border-emerald-300' : verdict === 'wrong' ? 'border-red-300' : undefined}>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0 pb-2">
        <CardTitle className="min-w-0 break-words font-mono text-xs">{contestantLabel(result.contestant_key)}</CardTitle>
        <div className="flex shrink-0 items-center gap-2">
          {task !== 'sms' && (
            <DecisionChip decision={result.decision} verdict={verdict} errorText={result.status === 'ok' ? null : result.status.replace('_', ' ')} />
          )}
          {task !== 'sms' && result.status === 'ok' && result.decision && label?.decision !== result.decision && (
            <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" disabled={saving} onClick={() => onAccept(result.decision as string)}>
              <Check className="mr-1 h-3 w-3" /> This is right
            </Button>
          )}
          {task === 'sms' && result.status === 'ok' && (
            <>
              <Button size="icon" variant={rating === 'good' ? 'default' : 'outline'} className="h-7 w-7" disabled={saving} onClick={() => onRate('good')} aria-label="Good">
                <ThumbsUp className="h-3.5 w-3.5" />
              </Button>
              <Button size="icon" variant={rating === 'bad' ? 'destructive' : 'outline'} className="h-7 w-7" disabled={saving} onClick={() => onRate('bad')} aria-label="Bad">
                <ThumbsDown className="h-3.5 w-3.5" />
              </Button>
            </>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {result.status !== 'ok' && <p className="text-xs text-destructive">{result.error}</p>}
        {result.status === 'ok' && parsed && (task === 'analysis' || task === 'grace') && <AnalysisBody parsed={parsed} />}
        {result.status === 'ok' && parsed && task === 'gate' && <GateBody parsed={parsed} />}
        {result.status === 'ok' && parsed && task === 'sms' && <SmsBody parsed={parsed} />}

        <div className="flex flex-wrap gap-x-4 text-[11px] text-muted-foreground">
          <span>{formatMs(result.latency_ms)}</span>
          <span>{result.input_tokens ?? 0} in / {result.output_tokens ?? 0} out tokens</span>
          <span>{formatUsd(result.cost_usd)}</span>
        </div>

        {result.raw_text && task !== 'sms' && (
          <Collapsible>
            <CollapsibleTrigger className="text-[11px] text-muted-foreground underline">Raw response</CollapsibleTrigger>
            <CollapsibleContent>
              <pre className="mt-1 max-h-64 overflow-auto whitespace-pre-wrap rounded bg-muted p-2 text-[11px]">{result.raw_text}</pre>
            </CollapsibleContent>
          </Collapsible>
        )}
      </CardContent>
    </Card>
  )
}
